import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { publicWorkflow, workflowError, displayDate } from '../utils/electionWorkflows';

export default function NominationForm() {
  const { electionId } = useParams();
  const [election, setElection] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('new');
  const [form, setForm] = useState({
    positionId: '', candidateId: '', nomineeName: '', nomineeEmail: '',
    nominatorName: '', nominatorEmail: '', reason: '',
  });
  useEffect(() => {
    let active = true;
    setLoading(true);
    publicWorkflow(`nominations/${electionId}`)
      .then(response => { if (active) setElection(response.data.data); })
      .catch(err => { if (active) setError(workflowError(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [electionId]);
  const change = event => setForm({ ...form, [event.target.name]: event.target.value });
  const position = election?.positions.find(item => item.id === form.positionId);
  const submit = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const response = await publicWorkflow(`nominations/${electionId}`, {
        ...form, candidateId: mode === 'existing' ? form.candidateId : undefined,
        nomineeName: mode === 'new' ? form.nomineeName : undefined,
        nomineeEmail: mode === 'new' ? form.nomineeEmail : undefined,
      });
      setMessage(response.data.message);
    } catch (err) { setError(workflowError(err)); }
    finally { setBusy(false); }
  };
  return (
    <main className="container py-5" style={{ maxWidth: 760 }}>
      <Link to="/">VoteWave</Link>
      <h1 className="mt-3">Nominate a candidate</h1>
      {loading && <p role="status">Loading nomination form...</p>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {election && <>
        <h2 className="h4">{election.name}</h2>
        <p className="text-muted">Nominations close {displayDate(election.closesAt)}. Nominations are not votes.</p>
        {message ? <div className="alert alert-success" role="status">
          {message}
          <button className="btn btn-link d-block" onClick={() => setMessage('')}>Submit another nomination or request a new link</button>
        </div> : <form onSubmit={submit}>
          <p>Anyone with this link can nominate. Verify your email to have your nomination counted.
            You can nominate each person once per position. New nominees need organiser approval before joining the ballot.</p>
          <label className="form-label" htmlFor="nom-position">Position / category</label>
          <select id="nom-position" className="form-select mb-3" name="positionId" value={form.positionId}
            onChange={event => setForm({ ...form, positionId: event.target.value, candidateId: '' })} required>
            <option value="">Choose a position</option>
            {election.positions.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <label className="form-label" htmlFor="nom-mode">Nomination type</label>
          <select id="nom-mode" className="form-select mb-3" value={mode} onChange={event => setMode(event.target.value)}>
            <option value="new">Propose a new person</option>
            <option value="existing">Endorse an existing candidate</option>
          </select>
          {mode === 'existing' ? <>
            <label className="form-label" htmlFor="nom-candidate">Candidate</label>
            <select id="nom-candidate" className="form-select mb-3" name="candidateId" value={form.candidateId} onChange={change} required>
              <option value="">Choose a candidate</option>
              {position?.candidates.map(item => <option key={item.id} value={item.id}>{item.fullName}</option>)}
            </select>
            {position && !position.candidates.length && <p>No approved candidates in this position yet. You can propose someone new.</p>}
          </> : <>
            <label className="form-label" htmlFor="nom-name">Nominee’s full name</label>
            <input id="nom-name" className="form-control mb-3" name="nomineeName" value={form.nomineeName} onChange={change} maxLength={255} required />
            <label className="form-label" htmlFor="nom-email">Nominee’s email</label>
            <input id="nom-email" className="form-control mb-3" type="email" name="nomineeEmail" value={form.nomineeEmail} onChange={change} maxLength={254} required />
          </>}
          <label className="form-label" htmlFor="nominator-name">Your full name</label>
          <input id="nominator-name" className="form-control mb-3" name="nominatorName" value={form.nominatorName} onChange={change} maxLength={255} required />
          <label className="form-label" htmlFor="nominator-email">Your email</label>
          <input id="nominator-email" className="form-control mb-3" type="email" name="nominatorEmail" value={form.nominatorEmail} onChange={change} maxLength={254} required />
          <label className="form-label" htmlFor="nom-reason">Why are you nominating this person? (optional)</label>
          <textarea id="nom-reason" className="form-control mb-3" name="reason" value={form.reason} onChange={change} maxLength={2000} rows={3} />
          <p className="small text-muted">Your details and reason are visible only to election administrators.
            Please ensure you have permission to share the nominee’s contact details.</p>
          <button className="btn btn-primary" disabled={busy || !election.positions.length}>{busy ? 'Submitting...' : 'Submit nomination'}</button>
        </form>}
      </>}
    </main>
  );
}
